import { SearchTermsStore } from './search-terms.store';
import { SearchTermsQuery } from './search-terms.query';
import { Injectable } from '@angular/core';
import { UUIDService } from '../../shared/uuid/uuid.service';
import { SearchTermName, SearchTermPluralName, SearchTermsContentType, SearchTermType } from './search-terms.enum';
import {
  aimsSearchTermTypesList,
  ChangeFilterTypeEvent,
  ESearchScopes,
  recordingsSearchTermTypesList,
  SearchTerm,
  worksSearchTermTypesList,
} from './search-term.model';
import { arrayRemove, transaction } from '@datorama/akita';
import { TypeaheadSearchApiService } from '../../shared/services/typeahead-search-api.service';
import { map, take, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { LocalStorageService } from 'src/app/shared/local-storage/local-storage.service';
import { FeatureFlagApiService } from '../../abstract/services/feature-flag-api.service';
import { FEATURE_FLAG } from '../../abstract/shared/enum';
import { cloneDeep } from 'lodash-es';

export interface SearchBarGroup {
  key: SearchTermType;
  value: any[];
  name: string;
  order: any;
}

interface IFiltersNamesMap {
  [key: string]: string;
}

interface IFitlersOrderingMap {
  [key: string]: number;
}

export const SEARCH_SCOPE_STORAGE_KEY = 'search_scope';

@Injectable({
  providedIn: 'root',
})
export class SearchTermsService {
  namesMap: IFiltersNamesMap = {
    ARTIST: 'ARTISTS',
    TITLE: 'RECORDINGS',
    WRITER: 'WRITERS',
    ISRC: 'ISRC',
    SONG_CODE: 'SONG CODE',
  };

  orderingMap: IFitlersOrderingMap = {
    ARTIST: 3,
    TITLE: 1,
    WRITER: 2,
    ISRC: 4,
    SONG_CODE: 5,
  };

  private externalSearchInfo: any = {};

  constructor(
    private searchTermsStore: SearchTermsStore,
    private uuid: UUIDService,
    private autocomplete: TypeaheadSearchApiService,
    private query: SearchTermsQuery,
    private localStorageService: LocalStorageService,
    private featureFlagApiService: FeatureFlagApiService
  ) {}

  getFilters(isOuterAction?: boolean): void {
    this.featureFlagApiService
      .getFlag(FEATURE_FLAG.ENABLE_AIMS)
      .pipe(
        take(1),
        tap(aimsFF => {
          const query = this.query.getValue();
          const isRecording = query.searchScope === ESearchScopes.RECORDINGS;
          const aimsIsEnabled = isRecording && aimsFF;
          const getFilters = (): SearchTerm => {
            if (aimsIsEnabled) {
              let currentSearchTerm: SearchTerm =
                cloneDeep(Object.values(query.entities)[0]) ?? this.getDefaultSearchTerm(aimsIsEnabled);
              if (
                ![SearchTermName.PROMPT, SearchTermName.EXTERNAL_LINK, SearchTermName.KEYWORD].includes(
                  currentSearchTerm?.name
                )
              ) {
                currentSearchTerm = this.getDefaultSearchTerm(aimsIsEnabled);
              }
              currentSearchTerm && (currentSearchTerm.selected.length = 0);
              return currentSearchTerm;
            }

            return this.getDefaultSearchTerm(aimsIsEnabled);
          };
          const term = isOuterAction ? this.getSearchTerm(aimsIsEnabled) : getFilters();
          this.searchTermsStore.set([term]);
        })
      )
      .subscribe();
  }

  clearEntireSearch(isOuterAction?: boolean): void {
    this.getFilters(isOuterAction);
  }

  addNewFilter(): void {
    this.searchTermsStore.add(this.getDefaultSearchTerm());
  }

  get activeFilter(): SearchTerm | SearchTerm[] {
    return this.query.getActive();
  }

  setActive(id: string): void {
    this.searchTermsStore.setActive(id);
  }

  updateActive(filter: Partial<SearchTerm>): void {
    this.searchTermsStore.updateActive(filter);
  }

  set externalSearch(data: any) {
    this.externalSearchInfo = data;
  }

  @transaction()
  addSelectedValueActive(
    value: string,
    contentType: SearchTermsContentType = null,
    type: SearchTermType = null,
    replaceSelected: boolean = false,
    aimsIsEnabled: boolean = false
  ): void {
    this.updateFilterType({ type }, aimsIsEnabled);

    if (contentType) {
      this.updateContentType({ contentType });
    }

    const activeFilterId: string = this.query.getActiveId();
    const activeFilter: SearchTerm = this.query.getEntity(activeFilterId);

    const { selected: current = [] } = activeFilter;
    const currentSelected = Array.isArray(current) ? current : [];
    let selected: Array<string>;
    if (replaceSelected) {
      selected = [value];
    } else {
      selected = currentSelected.find(v => v === value) ? currentSelected : [].concat(currentSelected, [value]);
    }

    this.searchTermsStore.update(activeFilterId, {
      selected,
      contentType: contentType ? contentType : activeFilter.contentType,
    });
  }

  @transaction()
  updateFilterType(event: ChangeFilterTypeEvent, aimsIsEnabled: boolean = false): void {
    const { type, filter } = event;
    const searchTermTypesList =
      this.query.getValue().searchScope === ESearchScopes.RECORDINGS
        ? aimsIsEnabled
          ? aimsSearchTermTypesList
          : recordingsSearchTermTypesList
        : worksSearchTermTypesList;

    const searchType = searchTermTypesList.find(st => st.type === type);

    if (!searchType) {
      return;
    }
    if (filter) {
      this.setActive(filter.id);
    }

    const { name, pluralName } = searchType;

    this.updateActive({
      name,
      type,
      pluralName,
      options: null,
    });
  }

  @transaction()
  updateContentType({ contentType }: Partial<SearchTerm>): void {
    this.updateActive({
      contentType,
    });
  }

  getAutocomplete(terms: string, type: SearchTermType, filterId: string): Observable<SearchBarGroup[]> {
    if (terms === '' || ['LYRICS', 'ALBUM'].includes(type)) {
      this.searchTermsStore.update(filterId, {
        options: undefined,
      });

      return of([]);
    }

    const isAimsSearch = [SearchTermType.ExternalLink, SearchTermType.Prompt].includes(type);

    const autocomplete$ =
      this.query.getValue().searchScope === ESearchScopes.RECORDINGS
        ? isAimsSearch
          ? of([])
          : this.autocomplete.suggestRecordings(terms, type)
        : this.autocomplete.suggestWorks(terms, type);

    return autocomplete$.pipe(
      map(options => {
        return options
          ?.map(group => ({
            key: group.searchType,
            value: group.values,
            name: this.namesMap[group.searchType],
            order: this.orderingMap[group.searchType],
          }))
          .sort((a, b) => a.order - b.order)
          .slice(0, 3);
      }),
      tap(options => {
        !isAimsSearch &&
          this.searchTermsStore.update(filterId, {
            options,
          });
      })
    );
  }

  removeSelectedActive(value: any): void {
    this.searchTermsStore.updateActive(({ selected: current, contentType }) => {
      const selected = arrayRemove(current, value);

      const type = selected.length === 0 ? SearchTermsContentType.Tag : contentType;
      return {
        selected,
        contentType: type,
      };
    });
  }

  clearAllActive(): void {
    this.searchTermsStore.updateActive(() => ({
      selected: [],
      contentType: SearchTermsContentType.Tag,
    }));
  }

  openSearchArea(): void {
    this.searchTermsStore.update(state => ({
      ...state,
      ui: {
        ...state.ui,
        searchAreaActive: true,
      },
    }));
  }

  closeSearchArea(): void {
    this.searchTermsStore.update(state => ({
      ...state,
      ui: {
        ...state.ui,
        searchAreaActive: false,
      },
    }));
  }

  toggleSearchArea(): void {
    this.searchTermsStore.update(state => ({
      ...state,
      ui: {
        ...state.ui,
        searchAreaActive: !state.ui.searchAreaActive,
      },
    }));
  }

  @transaction()
  setSearchScope(scope: ESearchScopes, aimsIsEnabled: boolean = false): void {
    this.searchTermsStore.update(state => ({
      ...state,
      ui: {
        ...state.ui,
      },
      searchScope: scope,
      searchTypes:
        scope === ESearchScopes.RECORDINGS
          ? aimsIsEnabled
            ? aimsSearchTermTypesList
            : recordingsSearchTermTypesList
          : worksSearchTermTypesList,
    }));
    this.clearEntireSearch();
    this.localStorageService.set(SEARCH_SCOPE_STORAGE_KEY, scope);
  }

  getDefaultSearchTerm(aimsIsEnabled: boolean = false): SearchTerm {
    const currentSearchScope: ESearchScopes = this.query.getValue().searchScope;
    const filterPlaceholder =
      currentSearchScope === ESearchScopes.RECORDINGS ? SearchTermPluralName.Term : SearchTermPluralName.WorkTerm;

    return {
      id: this.uuid.get(),
      type: aimsIsEnabled ? SearchTermType.Keyword : SearchTermType.Term,
      pluralName: aimsIsEnabled ? SearchTermPluralName.KEYWORD : filterPlaceholder,
      name: aimsIsEnabled ? SearchTermName.KEYWORD : SearchTermName.Term,
      selected: [],
      options: null,
      contentType: aimsIsEnabled ? SearchTermsContentType.Prompt : SearchTermsContentType.Tag,
    };
  }

  getSearchTerm(aimsIsEnabled: boolean = false): SearchTerm {
    const currentSearchScope: ESearchScopes = this.query.getValue().searchScope;
    const filterPlaceholder =
      currentSearchScope === ESearchScopes.RECORDINGS ? SearchTermPluralName.Term : SearchTermPluralName.WorkTerm;
    const type = this.externalSearchInfo
      ? SearchTermType.Keyword
      : aimsIsEnabled
      ? SearchTermType.Prompt
      : SearchTermType.Term;
    const pluralName = this.externalSearchInfo
      ? SearchTermPluralName.KEYWORD
      : aimsIsEnabled
      ? SearchTermPluralName.PROMPT
      : filterPlaceholder;
    const name = this.externalSearchInfo
      ? SearchTermName.KEYWORD
      : aimsIsEnabled
      ? SearchTermName.PROMPT
      : SearchTermName.Term;

    return {
      id: this.uuid.get(),
      type,
      pluralName,
      name,
      selected: [],
      options: null,
      contentType:
        aimsIsEnabled || this.externalSearchInfo ? SearchTermsContentType.Prompt : SearchTermsContentType.Query,
      // ...this.externalSearchInfo,
    };
  }
}
